/**
 * AutoGlow · Advisor React Query hooks · Sprint T-13.
 *
 * Each hook = 1 endpoint = 1 widget. Centralized cache config.
 *
 * NO business logic · NO derived state · NO calculations.
 * The frontend ONLY consumes intelligence from backend.
 */
import { useQuery } from "@tanstack/react-query";
import { advisorApi } from "../services/advisorApi";

const FIVE_MIN = 5 * 60 * 1000;
const TEN_MIN = 10 * 60 * 1000;
const ONE_HOUR = 60 * 60 * 1000;

const baseOpts = {
  staleTime: FIVE_MIN,
  gcTime: TEN_MIN,
  retry: 1,
  refetchOnWindowFocus: false,
};

// ---------------------------------------------------------------------------
// Org resolver · slug → public payload (used by /centro/advisor/:slug)
// ---------------------------------------------------------------------------

export const useOrgBySlug = (slug) =>
  useQuery({
    queryKey: ["org.public", slug],
    queryFn: () => advisorApi.org.publicBySlug(slug).then((r) => r.data),
    enabled: !!slug,
    staleTime: ONE_HOUR,
    ...baseOpts,
  });

export const useSuperadminOrgs = (q = "", limit = 50, enabled = true) =>
  useQuery({
    queryKey: ["org.marketplace.list", q, limit],
    queryFn: () => advisorApi.org.marketplaceList(limit).then((r) => r.data),
    enabled,
    staleTime: FIVE_MIN,
    ...baseOpts,
  });

// ---------------------------------------------------------------------------
// AG-01 hooks
// ---------------------------------------------------------------------------

export const useBusinessReport = (orgId, { withNarrative = false, competitorId, enabled = true } = {}) =>
  useQuery({
    queryKey: ["business.report", orgId, withNarrative, competitorId],
    queryFn: () => advisorApi.business.report(orgId, withNarrative, competitorId).then((r) => r.data),
    enabled: !!orgId && enabled,
    staleTime: withNarrative ? ONE_HOUR : FIVE_MIN,
    ...baseOpts,
  });

export const useBusinessScore = (orgId) =>
  useQuery({
    queryKey: ["business.score", orgId],
    queryFn: () => advisorApi.business.score(orgId).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

export const useBusinessRoadmap = (orgId) =>
  useQuery({
    queryKey: ["business.roadmap", orgId],
    queryFn: () => advisorApi.business.roadmap(orgId).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

export const useBusinessPriorities = (orgId) =>
  useQuery({
    queryKey: ["business.priorities", orgId],
    queryFn: () => advisorApi.business.priorities(orgId).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

export const useBusinessSummary = (orgId, withNarrative = false) =>
  useQuery({
    queryKey: ["business.summary", orgId, withNarrative],
    queryFn: () => advisorApi.business.summary(orgId, withNarrative).then((r) => r.data),
    enabled: !!orgId,
    staleTime: withNarrative ? ONE_HOUR : FIVE_MIN,
    ...baseOpts,
  });

// ---------------------------------------------------------------------------
// AG-02 hooks
// ---------------------------------------------------------------------------

export const useGrowthFunnel = (orgId) =>
  useQuery({
    queryKey: ["growth.funnel", orgId],
    queryFn: () => advisorApi.growth.funnel(orgId).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

export const useGrowthExperiments = (orgId, competitorId) =>
  useQuery({
    queryKey: ["growth.experiments", orgId, competitorId],
    queryFn: () => advisorApi.growth.experiments(orgId, competitorId).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

export const useGrowthPlan = (orgId, horizon = "monthly") =>
  useQuery({
    queryKey: ["growth.plan", orgId, horizon],
    queryFn: () => advisorApi.growth.plan(orgId, horizon).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

export const useGrowthKpis = (orgId) =>
  useQuery({
    queryKey: ["growth.kpis", orgId],
    queryFn: () => advisorApi.growth.kpis(orgId).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

export const useGrowthForecast = (orgId, horizon = "monthly") =>
  useQuery({
    queryKey: ["growth.forecast", orgId, horizon],
    queryFn: () => advisorApi.growth.forecast(orgId, horizon).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

export const useGrowthReport = (orgId, opts = {}) =>
  useQuery({
    queryKey: ["growth.report", orgId, opts.horizon || "monthly", !!opts.with_narrative, opts.competitor_id],
    queryFn: () => advisorApi.growth.report(orgId, opts).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

// ---------------------------------------------------------------------------
// AG-03 · SEO (code-based · per taxonomy node, not per org)
// ---------------------------------------------------------------------------

export const useSeoBrief = (code) =>
  useQuery({
    queryKey: ["seo.brief", code],
    queryFn: () => advisorApi.seo.brief(code).then((r) => r.data),
    enabled: !!code,
    ...baseOpts,
  });

export const useSeoScore = (code) =>
  useQuery({
    queryKey: ["seo.score", code],
    queryFn: () => advisorApi.seo.score(code).then((r) => r.data),
    enabled: !!code,
    ...baseOpts,
  });

export const useSeoLinks = (code) =>
  useQuery({
    queryKey: ["seo.links", code],
    queryFn: () => advisorApi.seo.links(code).then((r) => r.data),
    enabled: !!code,
    ...baseOpts,
  });

export const useSeoFullReport = (code) =>
  useQuery({
    queryKey: ["seo.fullReport", code],
    queryFn: () => advisorApi.seo.fullReport(code).then((r) => r.data),
    enabled: !!code,
    ...baseOpts,
  });

// ---------------------------------------------------------------------------
// AG-04 · Content (code-based preview)
// ---------------------------------------------------------------------------

export const useContentPreview = (code, contentType = "landing") =>
  useQuery({
    queryKey: ["content.preview", code, contentType],
    queryFn: () => advisorApi.content.preview(code, contentType).then((r) => r.data),
    enabled: !!code,
    staleTime: ONE_HOUR,
    ...baseOpts,
  });

// ---------------------------------------------------------------------------
// AG-05 · Competitor (org-based · profile/opportunities/market) + (focal/competitor pair)
// ---------------------------------------------------------------------------

export const useCompetitorProfile = (orgId) =>
  useQuery({
    queryKey: ["competitor.profile", orgId],
    queryFn: () => advisorApi.competitor.profile(orgId).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

export const useCompetitorCandidates = (orgId, limit = 10) =>
  useQuery({
    queryKey: ["competitor.candidates", orgId, limit],
    queryFn: () => advisorApi.competitor.candidates(orgId, limit).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

export const useCompetitorOpportunities = (orgId, limit = 20) =>
  useQuery({
    queryKey: ["competitor.opportunities", orgId, limit],
    queryFn: () => advisorApi.competitor.opportunities(orgId, limit).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

export const useCompetitorMarket = (orgId, limit = 15) =>
  useQuery({
    queryKey: ["competitor.market", orgId, limit],
    queryFn: () => advisorApi.competitor.market(orgId, limit).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

export const useCompetitorSwot = (orgId, competitorId) =>
  useQuery({
    queryKey: ["competitor.swot", orgId, competitorId],
    queryFn: () => advisorApi.competitor.swot(orgId, competitorId).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

export const useCompetitorGap = (focal, competitor) =>
  useQuery({
    queryKey: ["competitor.gap", focal, competitor],
    queryFn: () => advisorApi.competitor.gap(focal, competitor).then((r) => r.data),
    enabled: !!focal && !!competitor,
    ...baseOpts,
  });

// ---------------------------------------------------------------------------
// AG-06 · Marketplace
// ---------------------------------------------------------------------------

export const useMarketplaceInfo = () =>
  useQuery({
    queryKey: ["marketplace.info"],
    queryFn: () => advisorApi.marketplace.info().then((r) => r.data),
    ...baseOpts,
    staleTime: ONE_HOUR,
  });

export const useEcosystemOverview = (enabled = true) =>
  useQuery({
    queryKey: ["marketplace.ecosystem"],
    queryFn: () => advisorApi.marketplace.ecosystemOverview().then((r) => r.data),
    enabled,
    ...baseOpts,
  });

export const useSectorIntelligence = (sectorCode) =>
  useQuery({
    queryKey: ["marketplace.sector", sectorCode],
    queryFn: () => advisorApi.marketplace.sectorIntelligence(sectorCode).then((r) => r.data),
    enabled: !!sectorCode,
    ...baseOpts,
  });

export const useGeoIntelligence = (city) =>
  useQuery({
    queryKey: ["marketplace.geo", city],
    queryFn: () => advisorApi.marketplace.geoIntelligence(city).then((r) => r.data),
    enabled: !!city,
    ...baseOpts,
  });

export const useLeaderboard = (metric = "funnel_health", limit = 10) =>
  useQuery({
    queryKey: ["marketplace.leaderboard", metric, limit],
    queryFn: () => advisorApi.marketplace.leaderboard(metric, limit).then((r) => r.data),
    ...baseOpts,
  });

export const useBenchmark = (orgId, metric = "funnel_health") =>
  useQuery({
    queryKey: ["marketplace.benchmark", orgId, metric],
    queryFn: () => advisorApi.marketplace.benchmark(orgId, metric).then((r) => r.data),
    enabled: !!orgId,
    ...baseOpts,
  });

export const useDemandSupplyGap = (enabled = true) =>
  useQuery({
    queryKey: ["marketplace.demandSupply"],
    queryFn: () => advisorApi.marketplace.demandSupplyGap().then((r) => r.data),
    enabled,
    ...baseOpts,
  });

export const useTrends = (horizonDays = 30, enabled = true) =>
  useQuery({
    queryKey: ["marketplace.trends", horizonDays],
    queryFn: () => advisorApi.marketplace.trends(horizonDays).then((r) => r.data),
    enabled,
    ...baseOpts,
  });
